import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  BookOpen, Play, FileText, Shield, Target, Ear, CheckCircle, Lock,
  ChevronRight, Award, Clock, ArrowLeft, Star, AlertTriangle, 
  ChevronDown, ChevronUp, XCircle, Loader2
} from 'lucide-react';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  'book-open': BookOpen,
  'play': Play,
  'file-text': FileText,
  'shield': Shield,
  'target': Target,
  'ear': Ear
};

const ObserverTraining = () => {
  const navigate = useNavigate();
  const { moduleId } = useParams();
  const [modules, setModules] = useState([]);
  const [status, setStatus] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleContent, setModuleContent] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [view, setView] = useState('modules'); // modules, content, quiz

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadData();
  }, [navigate]);

  useEffect(() => {
    if (moduleId && modules.length > 0) {
      const mod = modules.find(m => m.id === moduleId);
      if (mod) {
        selectModule(mod);
      }
    }
  }, [moduleId, modules]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('observer_token');
      const [modulesRes, statusRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/observer/training/modules`),
        axios.get(`${BACKEND_URL}/api/observer/training/status`, { params: { token } })
      ]);
      
      setModules(modulesRes.data.modules || []);
      setStatus(statusRes.data);
    } catch (error) {
      console.error('Error loading training data:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectModule = async (module) => {
    setSelectedModule(module);
    setView('content');
    setQuizResult(null);
    setQuizAnswers({});
    
    try {
      const [contentRes, quizRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/observer/training/module/${module.id}`),
        axios.get(`${BACKEND_URL}/api/observer/training/quiz/${module.id}`)
      ]);
      
      setModuleContent(contentRes.data);
      setQuiz(quizRes.data);
    } catch (error) {
      console.error('Error loading module content:', error);
    }
  };

  const startQuiz = () => {
    setView('quiz');
    setQuizAnswers({});
    setQuizResult(null);
  };

  const submitQuiz = async () => {
    if (Object.keys(quizAnswers).length < quiz.questions.length) {
      alert('Please answer all questions');
      return;
    }
    
    setSubmittingQuiz(true);
    try {
      const token = localStorage.getItem('observer_token');
      const answersStr = quiz.questions.map((q, i) => quizAnswers[i] ?? 0).join(',');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/observer/training/quiz-submit`,
        null,
        {
          params: {
            token,
            module_id: selectedModule.id,
            answers: answersStr
          }
        }
      );
      
      setQuizResult(response.data);
      loadData(); // Refresh status
    } catch (error) {
      console.error('Error submitting quiz:', error);
      alert('Failed to submit quiz');
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const isModuleCompleted = (moduleId) => {
    return status?.modules_completed?.includes(moduleId);
  };

  const formatContent = (content) => {
    const lines = content.split('\n');
    return lines.map((line, idx) => {
      line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      
      if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
        return (
          <li key={idx} className="ml-4 text-gray-700" dangerouslySetInnerHTML={{ __html: line.replace(/^[•-]\s*/, '') }} />
        );
      }
      
      if (line.trim().startsWith('❌') || line.trim().startsWith('✅')) {
        const isNegative = line.trim().startsWith('❌');
        return (
          <div key={idx} className={`flex items-start gap-2 py-1 px-3 rounded ${isNegative ? 'bg-red-50' : 'bg-green-50'}`}>
            {isNegative ? <XCircle className="w-4 h-4 text-red-500 mt-0.5" /> : <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />}
            <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: line.replace(/^[❌✅]\s*/, '') }} />
          </div>
        );
      }
      
      if (/^\d+\./.test(line.trim())) {
        return (
          <li key={idx} className="ml-4 text-gray-700 list-decimal" dangerouslySetInnerHTML={{ __html: line.replace(/^\d+\.\s*/, '') }} />
        );
      }
      
      if (line.trim().startsWith('☐')) {
        return (
          <div key={idx} className="flex items-center gap-2 py-1">
            <div className="w-4 h-4 border-2 border-gray-400 rounded" />
            <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: line.replace('☐', '') }} />
          </div>
        );
      }
      
      if (!line.trim()) return <div key={idx} className="h-2" />;
      
      return <p key={idx} className="text-gray-700 my-1" dangerouslySetInnerHTML={{ __html: line }} />;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
        </div>
      </div>
    );
  }

  // Quiz View
  if (view === 'quiz' && quiz) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 pb-8 px-4">
          <div className="max-w-3xl mx-auto">
            <Button variant="ghost" onClick={() => setView('content')} className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Module
            </Button>
            
            <Card className="mb-6">
              <CardContent className="p-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{quiz.title}</h1>
                <p className="text-gray-600">Answer all questions. You need {quiz.passing_score}% to pass.</p>
              </CardContent>
            </Card>

            {quizResult ? (
              <Card className={`mb-6 border-l-4 ${quizResult.passed ? 'border-l-green-500' : 'border-l-red-500'}`}>
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    {quizResult.passed ? (
                      <Award className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    ) : (
                      <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    )}
                    <h2 className="text-2xl font-bold">{quizResult.passed ? 'Congratulations!' : 'Keep Learning!'}</h2>
                    <p className="text-4xl font-bold mt-2" style={{ color: quizResult.passed ? '#22c55e' : '#ef4444' }}>
                      {quizResult.score.toFixed(0)}%
                    </p>
                    <p className="text-gray-600 mt-2">
                      {quizResult.correct} of {quizResult.total} correct
                    </p>
                  </div>
                  
                  {quizResult.passed ? (
                    <Button onClick={() => { setView('modules'); setSelectedModule(null); }} className="w-full bg-green-600 hover:bg-green-700">
                      <CheckCircle className="w-4 h-4 mr-2" /> Continue to Next Module
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <Button onClick={() => setView('content')} variant="outline" className="w-full">
                        Review Module Content
                      </Button>
                      <Button onClick={() => { setQuizResult(null); setQuizAnswers({}); }} className="w-full bg-violet-600 hover:bg-violet-700">
                        Try Again
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <>
                {quiz.questions.map((q, qIndex) => (
                  <Card key={q.id} className="mb-4">
                    <CardContent className="p-6">
                      <p className="font-semibold text-gray-900 mb-4">
                        {qIndex + 1}. {q.question}
                      </p>
                      <div className="space-y-2">
                        {q.options.map((option, oIndex) => (
                          <button
                            key={oIndex}
                            onClick={() => setQuizAnswers({ ...quizAnswers, [qIndex]: oIndex })}
                            className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                              quizAnswers[qIndex] === oIndex
                                ? 'border-violet-500 bg-violet-50'
                                : 'border-gray-200 hover:border-violet-300'
                            }`}
                          >
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full mr-3 text-sm ${
                              quizAnswers[qIndex] === oIndex
                                ? 'bg-violet-500 text-white'
                                : 'bg-gray-200 text-gray-600'
                            }`}>
                              {String.fromCharCode(65 + oIndex)}
                            </span>
                            {option}
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                <Button
                  onClick={submitQuiz}
                  disabled={submittingQuiz || Object.keys(quizAnswers).length < quiz.questions.length}
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white py-6 text-lg"
                >
                  {submittingQuiz ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <>Submit Quiz ({Object.keys(quizAnswers).length}/{quiz.questions.length} answered)</>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Module Content View
  if (view === 'content' && selectedModule && moduleContent) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 pb-8 px-4">
          <div className="max-w-4xl mx-auto">
            <Button variant="ghost" onClick={() => { setView('modules'); setSelectedModule(null); }} className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Modules
            </Button>
            
            {/* Module Header */}
            <Card className="mb-6 bg-gradient-to-r from-violet-500 to-indigo-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                    {React.createElement(iconMap[selectedModule.icon] || BookOpen, { className: "w-8 h-8" })}
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">{moduleContent.title}</h1>
                    <p className="text-violet-100">{selectedModule.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-violet-200">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" /> {selectedModule.duration_minutes} min
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="w-4 h-4" /> {moduleContent.sections?.length || 0} sections
                      </span>
                      {isModuleCompleted(selectedModule.id) && (
                        <span className="flex items-center gap-1 bg-green-500 px-2 py-0.5 rounded-full">
                          <CheckCircle className="w-4 h-4" /> Completed
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Content Sections */}
            <div className="space-y-4 mb-6">
              {moduleContent.sections?.map((section, idx) => (
                <Card key={idx} className="overflow-hidden">
                  <button
                    onClick={() => setExpandedSection(expandedSection === idx ? null : idx)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 bg-violet-100 text-violet-600 rounded-full flex items-center justify-center font-semibold">
                        {idx + 1}
                      </span>
                      <span className="font-semibold text-gray-900">{section.title}</span>
                    </div>
                    {expandedSection === idx ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  {expandedSection === idx && (
                    <CardContent className="px-6 pb-6 pt-0 border-t">
                      <div className="prose max-w-none mt-4">
                        {formatContent(section.content)}
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>

            {/* Do's and Don'ts */}
            {moduleContent.dos_and_donts && (
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" /> Do's
                    </h3>
                    <ul className="space-y-2">
                      {moduleContent.dos_and_donts.dos.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700 text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-red-500">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-red-800 mb-4 flex items-center gap-2">
                      <XCircle className="w-5 h-5" /> Don'ts
                    </h3>
                    <ul className="space-y-2">
                      {moduleContent.dos_and_donts.donts.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700 text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Take Quiz Button */}
            <Card className="bg-gradient-to-r from-violet-50 to-indigo-50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Ready to test your knowledge?</h3>
                    <p className="text-gray-600 text-sm">Complete the quiz to finish this module</p>
                  </div>
                  <Button onClick={startQuiz} className="bg-violet-600 hover:bg-violet-700 text-white">
                    <Play className="w-4 h-4 mr-2" /> Take Quiz
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Modules List View (default)
  return (
    <div className="min-h-screen bg-gray-50">
      <ObserverNav />
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <BookOpen className="w-7 h-7 text-violet-600" />
                Observer Training
              </h1>
              <p className="text-gray-600">Complete all modules to become a certified Observer</p>
            </div>
          </div>

          {/* Progress Card */}
          {status && (
            <Card className="mb-6 border-l-4 border-l-violet-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      status.training_complete ? 'bg-green-100' : 'bg-violet-100'
                    }`}>
                      {status.training_complete ? (
                        <Award className="w-6 h-6 text-green-600" />
                      ) : (
                        <Target className="w-6 h-6 text-violet-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {status.training_complete ? 'Training Complete!' : 'Training Progress'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {status.completed_modules} of {status.total_modules} modules completed
                      </p>
                    </div>
                  </div>
                  {status.training_complete && (
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Average Score</p>
                      <p className="text-2xl font-bold text-green-600">{status.average_quiz_score}%</p>
                    </div>
                  )}
                </div>
                <Progress value={status.completion_percentage} className="h-2" />
                <p className="text-right text-sm text-gray-500 mt-2">{status.completion_percentage}% complete</p>
              </CardContent>
            </Card>
          )}

          {/* Certification Badge */}
          {status?.certification_eligible && (
            <Card className="mb-6 bg-gradient-to-r from-yellow-400 to-orange-500 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Star className="w-12 h-12" />
                  <div>
                    <h3 className="font-bold text-lg">Certification Eligible!</h3>
                    <p className="text-yellow-100">You've completed all training with a passing score. You're now certified to start observing!</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Modules List */}
          <div className="space-y-4">
            {modules.map((module, idx) => {
              const Icon = iconMap[module.icon] || BookOpen;
              const completed = isModuleCompleted(module.id);
              const isLocked = idx > 0 && !isModuleCompleted(modules[idx - 1].id);
              
              return (
                <Card 
                  key={module.id}
                  className={`overflow-hidden transition-all ${
                    completed ? 'border-l-4 border-l-green-500' : ''
                  } ${isLocked ? 'opacity-60' : 'hover:shadow-md cursor-pointer'}`}
                  onClick={() => !isLocked && selectModule(module)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                          completed ? 'bg-green-100' : isLocked ? 'bg-gray-100' : 'bg-violet-100'
                        }`}>
                          {completed ? (
                            <CheckCircle className="w-7 h-7 text-green-600" />
                          ) : isLocked ? (
                            <Lock className="w-7 h-7 text-gray-400" />
                          ) : (
                            <Icon className="w-7 h-7 text-violet-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">Module {idx + 1}</span>
                            {module.required && (
                              <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">Required</span>
                            )}
                          </div>
                          <h3 className="font-semibold text-gray-900">{module.title}</h3>
                          <p className="text-sm text-gray-500">{module.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {module.duration_minutes} min
                            </span>
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3" /> {module.lessons?.length || 0} lessons
                            </span>
                          </div>
                        </div>
                      </div>
                      {!isLocked && (
                        <ChevronRight className={`w-6 h-6 ${completed ? 'text-green-500' : 'text-gray-400'}`} />
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObserverTraining;
